const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.darksidedigital.com.au';
const logoUrl = 'https://storage.readdy-site.link/project_files/cb16f62b-b978-4a18-b0d4-78b46c2aa554/9ae0cf67-cf30-4444-9d14-012a7830e862_Darkside-Digital-logo-design.png?v=7acdaa383d113350b9c301867b0d13f1';

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Tourism Website Design Australia',
  description:
    'Darkside Digital specialises in tourism website design across Australia — reef trips, ferry services, adventure charters, dive operators, and accommodation. Websites built to win bookings, dominate Google, and appear in ChatGPT and Gemini AI search results.',
  url: `${siteUrl}/tourism-website-design`,
  provider: {
    '@type': 'LocalBusiness',
    name: 'Darkside Digital',
    url: siteUrl,
    telephone: '+611300029054',
    email: 'hello@darksidedigital.com.au',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Mission Beach',
      addressRegion: 'QLD',
      addressCountry: 'AU',
    },
    logo: logoUrl,
  },
  areaServed: { '@type': 'Country', name: 'Australia' },
  serviceType: 'Tourism Website Design',
  offers: [
    {
      '@type': 'Offer',
      name: 'Free Tourism Website Consultation',
      price: '0',
      priceCurrency: 'AUD',
      url: `${siteUrl}/tourism-website-design`,
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Tourism Website Design Australia | Darkside Digital',
  description:
    'Specialist tourism website design for Australian operators — reef trips, dive centres, ferry services, adventure charters, and accommodation. Rank on Google, win bookings, and get cited by AI.',
  url: `${siteUrl}/tourism-website-design`,
  dateModified: '2026-03-30',
  isPartOf: { '@type': 'WebSite', name: 'Darkside Digital', url: siteUrl },
  publisher: {
    '@type': 'Organization',
    name: 'Darkside Digital',
    logo: { '@type': 'ImageObject', url: logoUrl },
  },
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['#tourism-hero', '#tourism-pain-points', '#tourism-faq'],
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Tourism Website Design',
      item: `${siteUrl}/tourism-website-design`,
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do you specialise in tourism website design in Australia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — tourism is our core specialisation. Every one of our active clients is a tourism operator: reef trip companies, ferry services, dive centres, adventure charters, and accommodation providers, primarily across Far North Queensland and regional Australia. We understand seasonal demand patterns, aggregator competition, and how to win bookings from travellers searching on mobile.',
      },
    },
    {
      '@type': 'Question',
      name: 'Will my tourism website rank on Google?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We build SEO into every tourism website from day one — clean code structure, keyword-optimised content, schema markup, and local citation signals. Mission Beach Dive went from zero Google presence to #1 ranking in 52 days. Dunk Island Ferry claimed the top spot for their core terms before their first season was complete.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can my tourism business appear in ChatGPT and Gemini AI results?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes. We build GEO (Generative Engine Optimisation) into our tourism websites as standard — structuring your content, schema, and authority signals so that when a traveller asks ChatGPT 'who does reef trips near Mission Beach?' or Gemini 'best snorkel tours in Queensland', your business gets recommended. Mission Beach Adventures is now cited in ChatGPT and Google Gemini results.",
      },
    },
    {
      '@type': 'Question',
      name: 'Do you integrate online booking systems?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We integrate your existing booking platform (Rezdy, FareHarbor, Booking.com, Airbnb, or direct booking systems) into the website design so travellers can book without leaving your site. We also advise on reducing aggregator dependency and increasing direct bookings — which keeps more margin in your business.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long does a tourism website take to build?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most tourism websites are completed and launched within 3–5 weeks from the initial brief. We prioritise getting you live before your key season, then continue optimising for search and AI visibility after launch. Rush timelines are available for seasonal launches.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you work with tourism businesses outside Queensland?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. While we are based in Mission Beach and have deep roots in Far North Queensland tourism, we work with tourism and hospitality businesses all across Australia entirely remotely. Location is no barrier — our process is fully online from brief to launch.',
      },
    },
  ],
};

export default function TourismSchemaMarkup() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}
